import { PkButton, PkSpinner } from '@uefadigital/panenka-react';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';

import { useLivescoresTabs } from '../../hooks/tabs';
import type { LivescoreTab } from '../../store/types';
import { getTabIntesection, scrollToTab } from '../../utils/scroll';

import DrawTab from './DrawTab';
import MatchesTab from './MatchesTab';

import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';

import * as classes from './styles.module.scss';

dayjs.extend(isSameOrAfter);

const DatesList = () => {
  const {
    selectedTab,
    setSelectedTab,
    tabs,
    isLoading,
    isFetchingPreviousPage,
    hasPreviousPage,
    loadPrevious,
    loadNext,
  } = useLivescoresTabs();
  const container = useRef<HTMLDivElement>(null);
  const scrollSpy = useRef<HTMLDivElement>(null);
  const DOMSize = useResizeObserver(container, 200);
  const scrolled = useViewportSpy(scrollSpy, { rootMargin: '0px 0px 0px 25%' });
  const onScroll = useWindowScroll();
  const t = useTranslateFn();

  const currentTabId = useRef<string>(null);
  const [initialTab, setInitialTab] = useState<LivescoreTab>();

  useEffect(() => {
    if (initialTab || !tabs.length || !selectedTab) return;
    setInitialTab(selectedTab);
  }, [initialTab, selectedTab, tabs.length]);

  useEffect(() => {
    if (!initialTab) return;

    scrollToTab(initialTab.id);
    currentTabId.current = initialTab.id;
  }, [initialTab]);

  const loadPrevDates = () => {
    if (!tabs.length) return;

    loadPrevious();
  };

  useEffect(() => {
    if (!scrolled) return;

    loadNext();
  }, [loadNext, scrolled]);

  const tabsRefs = useRef(new Map<string, HTMLDivElement>());
  const scrollTimeout = useRef<number>(undefined);
  const scrollCallback = useCallback(() => {
    if (!initialTab) return;

    scrollTimeout.current ??= requestAnimationFrame(() => {
      const inviewEls = Array.from(tabsRefs.current.entries())
        .map(([id, el]) => getTabIntesection(id, el))
        // filter visible tabs
        .filter(({ intersectRatio }) => intersectRatio > 0)
        // sort by intersection ratio DESC and boundingClientRect.top ASC
        .toSorted((a, b) => {
          if (a.intersectRatio !== b.intersectRatio) return b.intersectRatio - a.intersectRatio;

          return a.top - b.top;
        });

      const tabId = inviewEls.at(0)?.id;
      const tab = tabId && tabs.find((t) => t.id === tabId);
      if (tab) {
        setSelectedTab(tab);
        currentTabId.current = tab.id;
      }

      cancelAnimationFrame(scrollTimeout.current);
      scrollTimeout.current = null;
    });
  }, [initialTab, setSelectedTab, tabs]);

  onScroll(scrollCallback);

  useEffect(() => {
    if (!DOMSize?.height || !currentTabId.current) return;

    const currentTabEl = tabsRefs.current.get(currentTabId.current);
    const currentTabInview = currentTabEl && getTabIntesection(currentTabId.current, currentTabEl);
    if (currentTabInview?.intersectRatio > 0) return;

    scrollToTab(currentTabId.current);
  }, [DOMSize?.height]);

  useEffect(() => {
    if (isLoading || isFetchingPreviousPage || !currentTabId.current) return;

    scrollToTab(currentTabId.current);
  }, [isLoading, isFetchingPreviousPage]);

  const handleTabFocus = (tabId: string) => () => {
    const tab = tabId && tabs.find((t) => t.id === tabId);
    if (tab) {
      setSelectedTab(tab);
      currentTabId.current = tab.id;
    }
  };

  const focusTab = (tabId: string) => () => {
    document.getElementById(`calendar-tab-${tabId}`)?.focus();
  };

  const setRef = (ref: HTMLDivElement) => {
    if (!ref) return;
    tabsRefs.current.set(ref.dataset.tabId, ref);
  };

  return (
    <div ref={container} className={clsx('pk-col pk-col--span-12', classes.matchesContainer)}>
      {!!tabs.length && (
        <>
          {!hasPreviousPage ? (
            <div className='pk-align-self--center pk-font--base-regular pk-font-size--s pk-font-size-sm--m pk-text--text-02'>
              {t('no_prev_matches')}
            </div>
          ) : (
            <PkButton
              onPkClick={loadPrevDates}
              disabled={isFetchingPreviousPage ? true : undefined}
              appearance='outlined'
              className='pk-align-self--center'
              width='auto'
            >
              {t('load_prev_matches')}
            </PkButton>
          )}
        </>
      )}

      {isLoading && (
        <div className={classes.loading}>
          <PkSpinner />
        </div>
      )}

      {!isLoading &&
        tabs.map((tab) => {
          return (
            <Fragment key={tab.id}>
              <div
                className={clsx('livescores-calendar-tab', classes.tab)}
                ref={setRef}
                data-tab-id={tab.id}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                onFocus={handleTabFocus(tab.id)}
              >
                <button type='button' className='hideTitle showOnFocus' onClick={focusTab(tab.id)}>
                  {t('goto_tabs')}
                </button>
                {tab?.draw ? <DrawTab tab={tab} /> : <MatchesTab tab={tab} />}
              </div>
            </Fragment>
          );
        })}
      {!!tabs.length && <div ref={scrollSpy} className={classes.scrollSpy} />}
    </div>
  );
};

export { DatesList as PureDatesList };

export default withErrorBoundary(memo(DatesList));
